"use client";

import React, { useEffect, useRef } from "react";


export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    let frame = 0;
    let animId: number;

    const particles: { x: number; y: number; r: number; speed: number; opacity: number; color: string }[] = [];
    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.5 + 0.5,
        speed: Math.random() * 0.4 + 0.1,
        opacity: Math.random() * 0.5 + 0.1,
        color: Math.random() > 0.5 ? "#F2C94C" : "#29ABE2",
      });
    }

    function draw() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;

      particles.forEach((p) => {
        p.y -= p.speed;
        if (p.y < -5) {
          p.y = canvas.height + 5;
          p.x = Math.random() * canvas.width;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity * (0.6 + 0.4 * Math.sin(frame * 0.02 + p.x));
        ctx.fill();
      });

      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(draw);
    }

    draw();

    const handleResize = () => {
      if (!canvas) return;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section
      id="home"
      className="spotlight-hero grain-overlay relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ paddingTop: "80px" }}
    >
      {/* Particle Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0 }}
        aria-hidden="true"
      />

      {/* Spotlight Circles */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, rgba(242,201,76,0.1) 0%, transparent 70%)",
          animation: "spotlightPulse 5s ease-in-out infinite",
          zIndex: 1,
        }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, rgba(41,171,226,0.08) 0%, transparent 70%)",
          zIndex: 1,
        }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center flex flex-col items-center gap-8">
        {/* Badge */}
        <div
          className="badge-yellow inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest animate-fade-in-up animate-delay-1"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-hh-yellow inline-block" />
          Rajanukunte, Bengaluru
        </div>

        {/* Main Headline */}
        <h1
          className="font-display font-black leading-none tracking-tighter animate-fade-in-up animate-delay-2"
          style={{ fontSize: "clamp(3rem, 9vw, 7rem)" }}
        >
          Where Music
          <br />
          <span
            className="text-yellow"
            style={{
              textShadow: "0 0 60px rgba(242,201,76,0.4)",
            }}
          >
            Finds Its Voice
          </span>
        </h1>

        {/* Subheading */}
        <p
          className="text-lg md:text-xl max-w-2xl leading-relaxed animate-fade-in-up animate-delay-3"
          style={{ color: "rgba(255,255,255,0.65)" }}
        >
          Guitar. Vocals. Art & Craft. Jamming Sessions.{" "}
          <span style={{ color: "#29ABE2" }}>Hi-Hats</span> is Bangalore's most
          energetic music academy — where students don't just learn, they{" "}
          <em>perform</em>.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 items-center animate-fade-in-up animate-delay-4">
          <a
            href="#enroll"
            className="btn-yellow px-10 py-4 rounded-xl text-base font-display font-black tracking-wide inline-block"
          >
            🎵 Join the Jam
          </a>
          <a
            href="#courses"
            className="btn-outline px-10 py-4 rounded-xl text-base inline-block"
          >
            Explore Courses
          </a>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-6 mt-8 w-full max-w-xl animate-fade-in-up animate-delay-4">
          {[
            { value: "500+", label: "Students Trained" },
            { value: "8+", label: "Instruments Taught" },
            { value: "5★", label: "Community Rating" },
          ].map((stat) => (
            <div key={stat.label} className="stat-card rounded-2xl px-4 py-5 text-center">
              <p
                className="font-display font-black text-2xl md:text-3xl text-yellow"
              >
                {stat.value}
              </p>
              <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.45)" }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
        style={{ color: "rgba(255,255,255,0.3)" }}
      >
        <span className="text-xs font-display font-semibold uppercase tracking-widest">Scroll</span>
        <div
          className="w-px h-10"
          style={{
            background: "linear-gradient(to bottom, rgba(242,201,76,0.6), transparent)",
          }}
        />
      </div>
    </section>
  );
}